import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { findGlossaryTerm } from "@/data/glossary"
import type { SeoMeta } from "@/types"

type Props = {
  meta: SeoMeta
  slug: string
}

export default function GlossaryShow({ meta, slug }: Props) {
  const term = findGlossaryTerm(slug)

  if (!term) {
    return (
      <PublicLayout>
        <Head>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
          <link rel="canonical" href={meta.canonical} />
        </Head>
        <section className="px-6 py-24 md:py-32">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="font-display text-4xl font-bold text-charcoal">
              Term not found
              <span className="text-red">.</span>
            </h1>
            <p className="mt-4 text-lg text-slate">
              We couldn&rsquo;t find that glossary entry.
            </p>
            <Link
              href="/glossary"
              className="mt-8 inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
            >
              Back to glossary
            </Link>
          </div>
        </section>
      </PublicLayout>
    )
  }

  const seeAlso =
    term.seeAlso?.map((s) => ({ slug: s, entry: findGlossaryTerm(s) })).filter(
      (s): s is { slug: string; entry: NonNullable<ReturnType<typeof findGlossaryTerm>> } =>
        Boolean(s.entry),
    ) ?? []

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTerm",
            name: term.term,
            description: term.definition,
            url: meta.canonical,
            inDefinedTermSet: {
              "@type": "DefinedTermSet",
              name: "Property Marketing Glossary",
              url: `${new URL(meta.canonical).origin}/glossary`,
            },
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Glossary", path: "/glossary" },
            { name: term.term },
          ]}
        />
      </Head>

      <section className="px-6 py-20 md:py-28">
        <div className="mx-auto max-w-3xl">
          <Link
            href="/glossary"
            className="inline-flex items-center gap-1 text-xs font-medium uppercase tracking-[0.15em] text-red transition-colors hover:text-red/70"
          >
            &larr; Glossary
          </Link>
          <h1 className="mt-6 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
            {term.term}
            <span className="text-red">.</span>
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-slate">
            {term.definition}
          </p>

          {term.relatedPage && (
            <div className="mt-8">
              <Link
                href={term.relatedPage.href}
                className="inline-flex items-center gap-2 rounded-md border border-stone bg-white px-5 py-3 text-sm font-medium text-charcoal transition-colors hover:bg-stone"
              >
                {term.relatedPage.label}
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          )}
        </div>
      </section>

      {seeAlso.length > 0 && (
        <section className="border-t border-stone bg-cream px-6 py-16">
          <div className="mx-auto max-w-3xl">
            <ScrollReveal>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                See also
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold text-charcoal">
                Related terms
                <span className="text-red">.</span>
              </h2>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {seeAlso.map(({ slug, entry }) => (
                  <Link
                    key={slug}
                    href={`/glossary/${slug}`}
                    className="group block rounded-xl border border-stone bg-white p-5 transition-colors hover:border-red"
                  >
                    <p className="font-display text-base font-bold text-charcoal">
                      {entry.term}
                    </p>
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate">
                      {entry.definition}
                    </p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-red group-hover:gap-2 transition-all">
                      Read definition &rarr;
                    </span>
                  </Link>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      <section className="border-t border-stone px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Know the term, win the instruction
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
              Turn your property listings into scroll-stopping video ads. It
              takes 60 seconds.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
